import styled from 'styled-components';

import { useProfile } from 'context/profile';
import { simpleDate } from 'lib/date';
import { DenialCheckAction, DenialMatch } from 'models/DenialCheckAction';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import DenialListEmpty from 'svg/DenialListEmpty';
import DenialListFlag from 'svg/DenialListFlag';

import DetailSlideout from './DetailSlideout';

// The one-line summary shown in the DenialCheck row body. In a multi-action category the row
// itself is the clickable slideout trigger (the completed-DenialCheck branch of CategoryCard's
// rowItems map) — this summary is plain text, no box, no chevron.
export function DenialCheckSummary({ action }: { action: DenialCheckAction }) {
  const { profile } = useProfile();
  const orgName = profile.actingClient?.name || 'your organization';

  return (
    <SummaryText data-testid={action.isMatch ? 'denial-check-flag' : 'denial-check-result'}>
      {action.isMatch
        ? `This patient was identified in ${orgName}’s global denial list`
        : `Olio did not identify this patient in ${orgName}’s global denial list`}
    </SummaryText>
  );
}

// The DenialCheck detail slideout, shown on the COMPLETED action (the check runs autonomously and
// never asks a person to adjudicate — OLIO-2463). Controlled by the row that owns it: a flagged
// match lists the matched entries; a clear result shows the no-match state. Everything is read from
// the ACTION INSTANCE's own output (design §6c), never workflow-level values which can reflect a
// different (superseded) instance.
export default function DenialCheckReview({
  action,
  workflow,
  open,
  onClose,
}: {
  action: DenialCheckAction;
  workflow: Workflow;
  open: boolean;
  onClose: () => void;
}) {
  const { profile } = useProfile();
  const orgName = profile.actingClient?.name || 'your organization';
  const flagged = action.isMatch;
  const matches = action.matchedEntries;
  const checkedName = action.checkedName;

  return (
    <DetailSlideout
      open={open}
      onClose={onClose}
      title={`${workflow.patientName}: Denial List Check`}
      subtitle={`Referencing against ${orgName}’s global denial list for any matches`}>
      {flagged ? (
        <Cards>
          {matches.map((match) => (
            <MatchCard key={match.denialListItemId} match={match} orgName={orgName} />
          ))}
        </Cards>
      ) : (
        <EmptyState data-testid='denial-check-no-matches'>
          <DenialListEmpty width={40} height={40} />
          <EmptyTitle>No Matches Found for ‘{checkedName}’</EmptyTitle>
          <EmptyBody>Olio did not identify this patient in {orgName}’s global denial list</EmptyBody>
        </EmptyState>
      )}
    </DetailSlideout>
  );
}

function MatchCard({ match, orgName }: { match: DenialMatch; orgName: string }) {
  const dob = simpleDate(match.dateOfBirth);
  const sex = match.sex ? match.sex.charAt(0).toUpperCase() + match.sex.slice(1) : null;

  return (
    <Card data-testid='denial-match-card'>
      <CardHeader>
        <DenialListFlag width={14} height={16} />
        <CardHeaderText>A match was found on {orgName}’s global denial list</CardHeaderText>
      </CardHeader>
      <CardBody>
        <NameGroup>
          <MatchName>{match.patientName}</MatchName>
          {(dob || sex) && (
            <Demographics>
              {dob && `DOB: ${dob}`}
              {dob && sex && ', '}
              {sex && `Sex: ${sex}`}
            </Demographics>
          )}
        </NameGroup>
        {match.reason && (
          <Field>
            <FieldLabel>Reason</FieldLabel>
            <FieldValue>{match.reason}</FieldValue>
          </Field>
        )}
        {match.dateAdded && (
          <Field>
            <FieldLabel>Date Added</FieldLabel>
            <FieldValue>{simpleDate(match.dateAdded)}</FieldValue>
          </Field>
        )}
      </CardBody>
    </Card>
  );
}

const SummaryText = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: ${colors.red10};
`;

const CardHeaderText = styled(BodySmallBold)`
  color: ${colors.black100};
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
`;

const NameGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MatchName = styled(BodySmallBold)`
  font-size: 14px;
  color: ${colors.black100};
`;

const Demographics = styled(BodySmall)`
  color: ${colors.black75};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled(BodySmallBold)`
  color: ${colors.black100};
`;

const FieldValue = styled(BodySmall)`
  color: ${colors.black75};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  padding: 48px 24px;
  border-radius: 8px;
  background: ${colors.green10};
`;

const EmptyTitle = styled(BodySmallBold)`
  font-size: 16px;
  color: ${colors.black100};
  margin-top: 8px;
`;

const EmptyBody = styled(BodySmall)`
  color: ${colors.black75};
`;
